import { Card, Icon, Skeleton } from "@mui/material";
import PropTypes from "prop-types";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";

const buildPaginationItems = (page, pages) => {
  if (!pages || pages <= 1) return [1];

  const centerPages = [page - 1, page, page + 1].filter((value) => value > 1 && value < pages);
  const basePages = Array.from(new Set([1, ...centerPages, pages])).sort((a, b) => a - b);

  return basePages.reduce((acc, value, index) => {
    if (index > 0 && value - basePages[index - 1] > 1)
      acc.push(`ellipsis-${basePages[index - 1]}-${value}`);
    acc.push(value);
    return acc;
  }, []);
};

function CompactPagination({
  loading,
  length,
  total,
  pages,
  totalPages,
  page,
  setPage,
  onChange,
  noCard,
  variant,
}) {
  const resolvedPages = pages || totalPages || 0;
  const handleSetPage = setPage || onChange;
  const items = buildPaginationItems(page, resolvedPages);
  const selectorVariant = variant === "selector";
  const Container = noCard || selectorVariant ? SoftBox : Card;
  const containerProps = selectorVariant
    ? { sx: { width: "100%" } }
    : noCard
    ? { sx: { borderTop: "0.0625rem solid #e9ecef" } }
    : { sx: { mt: 2 } };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > resolvedPages || nextPage === page) return;
    if (typeof handleSetPage !== "function") return;
    handleSetPage(nextPage);
  };

  return (
    <Container {...containerProps}>
      <SoftBox
        display="flex"
        justifyContent="space-between"
        flexDirection={{ xs: selectorVariant ? "row" : "column", sm: "row" }}
        gap={{ xs: 1, md: selectorVariant ? 2 : 0 }}
        alignItems="center"
        textAlign="center"
        p={selectorVariant ? 0 : 3}
        py={selectorVariant ? 0 : 2}
      >
        {loading ? (
          <Skeleton variant="text" width={selectorVariant ? 100 : 170} />
        ) : selectorVariant ? (
          <SoftTypography
            variant="button"
            fontWeight="regular"
            sx={{
              color: "#71809f",
              fontSize: { xs: "0.75rem", sm: "0.8rem" },
              whiteSpace: "nowrap",
            }}
          >
            {total || 0} productos
          </SoftTypography>
        ) : (
          <SoftTypography variant="caption" color="dark">
            <SoftTypography variant="caption" color="dark" fontWeight="bold">
              {length || 0}
            </SoftTypography>{" "}
            de{" "}
            <SoftTypography variant="caption" color="dark" fontWeight="bold">
              {total || 0}
            </SoftTypography>
          </SoftTypography>
        )}

        {loading ? (
          <Skeleton variant="text" width={selectorVariant ? 230 : 170} />
        ) : (
          <SoftBox
            display="flex"
            alignItems="center"
            gap={selectorVariant ? { xs: 0.25, sm: 0.75 } : 0.5}
            flexWrap="wrap"
            justifyContent="center"
          >
            {selectorVariant ? (
              <SoftTypography
                variant="button"
                fontWeight="regular"
                sx={{
                  display: { xs: "none", md: "block" },
                  mr: 0.75,
                  color: "#596987",
                  fontSize: "0.8rem",
                  textTransform: "none",
                }}
              >
                <SoftTypography
                  component="span"
                  variant="button"
                  fontWeight="bold"
                  sx={{ color: "#20304e" }}
                >
                  {length || 0}
                </SoftTypography>{" "}
                de{" "}
                <SoftTypography
                  component="span"
                  variant="button"
                  fontWeight="bold"
                  sx={{ color: "#20304e" }}
                >
                  {total || 0}
                </SoftTypography>
              </SoftTypography>
            ) : null}

            <SoftButton
              variant={selectorVariant ? "outlined" : "text"}
              color={selectorVariant ? "secondary" : "dark"}
              iconOnly
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
              aria-label="Página anterior"
              sx={
                selectorVariant
                  ? {
                      minWidth: { xs: 32, sm: 34 },
                      width: { xs: 32, sm: 34 },
                      height: { xs: 32, sm: 34 },
                      p: 0,
                      borderColor: "#d5dde8",
                      borderRadius: "10px",
                      "&:disabled": { color: "#7b879e", borderColor: "#d5dde8", opacity: 1 },
                    }
                  : { minWidth: 24, width: 24, height: 24, p: 0 }
              }
            >
              <Icon
                sx={{ fontSize: selectorVariant ? "1.15rem !important" : "0.95rem !important" }}
              >
                chevron_left
              </Icon>
            </SoftButton>

            {items.map((item) =>
              typeof item === "string" ? (
                <SoftTypography
                  key={item}
                  variant="caption"
                  color="secondary"
                  sx={{ px: selectorVariant ? 0.5 : 0.25 }}
                >
                  ...
                </SoftTypography>
              ) : (
                <SoftButton
                  key={item}
                  variant={item === page ? "gradient" : "text"}
                  color={item === page ? "info" : "dark"}
                  onClick={() => handlePageChange(item)}
                  aria-label={`Ir a la página ${item}`}
                  aria-current={item === page ? "page" : undefined}
                  sx={{
                    minWidth: selectorVariant ? { xs: 32, sm: 34 } : 28,
                    width: selectorVariant ? { xs: 32, sm: 34 } : 28,
                    height: selectorVariant ? { xs: 32, sm: 34 } : 28,
                    p: 0,
                    borderRadius: selectorVariant ? "10px" : "999px",
                    fontSize: selectorVariant ? "0.8rem" : "0.75rem",
                    lineHeight: 1,
                    boxShadow:
                      selectorVariant && item === page
                        ? "0 4px 10px rgba(37, 99, 235, 0.25)"
                        : "none",
                  }}
                >
                  {item}
                </SoftButton>
              )
            )}

            <SoftButton
              variant={selectorVariant ? "outlined" : "text"}
              color={selectorVariant ? "secondary" : "dark"}
              iconOnly
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= resolvedPages}
              aria-label="Página siguiente"
              sx={
                selectorVariant
                  ? {
                      minWidth: { xs: 32, sm: 34 },
                      width: { xs: 32, sm: 34 },
                      height: { xs: 32, sm: 34 },
                      p: 0,
                      borderColor: "#d5dde8",
                      borderRadius: "10px",
                      "&:disabled": { color: "#aab4c3", borderColor: "#d5dde8", opacity: 1 },
                    }
                  : { minWidth: 24, width: 24, height: 24, p: 0 }
              }
            >
              <Icon
                sx={{ fontSize: selectorVariant ? "1.15rem !important" : "0.95rem !important" }}
              >
                chevron_right
              </Icon>
            </SoftButton>
          </SoftBox>
        )}
      </SoftBox>
    </Container>
  );
}

CompactPagination.propTypes = {
  loading: PropTypes.bool,
  length: PropTypes.number,
  total: PropTypes.number,
  pages: PropTypes.number,
  totalPages: PropTypes.number,
  page: PropTypes.number,
  setPage: PropTypes.func,
  onChange: PropTypes.func,
  noCard: PropTypes.bool,
  variant: PropTypes.oneOf(["default", "selector"]),
};

CompactPagination.defaultProps = {
  loading: false,
  length: 0,
  total: 0,
  pages: 0,
  totalPages: 0,
  page: 1,
  setPage: undefined,
  onChange: undefined,
  noCard: false,
  variant: "default",
};

export default CompactPagination;
